import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { doc, getDoc } from "firebase/firestore";

import styled from "styled-components";
import { TextField } from "@mui/material";
import { Container, Row, Col } from "react-bootstrap";

import {
  linkWithPhoneNumber,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";

import { Button } from "../../components/theme/Button";
import {
  Black,
  Blue,
  OffWhite,
  DarkGrey,
  Purple,
} from "../../components/theme/colors";

import { auth, db } from "../../services/firebase.config";
import { useAuth } from "../../services/AuthContext";

import "bootstrap/dist/css/bootstrap.min.css";

import logoImg from "../../images/logo/v.png";

const LoginBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 0 2vh;
  background: linear-gradient(120deg, ${Blue}, ${Purple});
  position: relative;
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 30vh;
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 100px;
  left: 2vh;
  right: 2vh;
`;

const LogoCircle = styled.div`
  width: 175px;
  height: 175px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Black};
`;

const cleanAndValidatePhoneNumber = (number) => {
  // Remove all non-numeric characters
  const cleanedNumber = number.replace(/\D/g, "");

  // Add +1 if not present
  const formattedNumber =
    cleanedNumber.length === 10 ? `+1${cleanedNumber}` : `+${cleanedNumber}`;

  // Validate for a US number (should have 11 digits and start with +1)
  const isValidUSNumber = /^\+1\d{10}$/.test(formattedNumber);

  if (!isValidUSNumber) {
    throw new Error("Please enter a valid US phone number.");
  }

  return formattedNumber;
};

const Login = () => {
  const { currentUserState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [nextStep, setNextStep] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Detects if this is a login to convert an anonymous account
  const isAnonymousLinking = location.state?.linkAnonymous;

  // Redirect if the user's auth state changes to logged-in.
  useEffect(() => {
    const handleLoginSuccess = async (user) => {
      const isWeb = Capacitor.getPlatform() === "web";
      const wasAnonymous = location.state?.linkAnonymous === true;

      console.log("🔍 handleLoginSuccess called with:", {
        isWeb,
        wasAnonymous,
        locationState: location.state,
        userIsAnonymous: user?.isAnonymous,
        userId: user?.uid,
      });

      // Web-specific navigation to funnel users to the app
      if (isWeb) {
        console.log("🌐 Web platform detected");

        // Check if this was an anonymous linking scenario
        if (wasAnonymous) {
          console.log(
            "✅ Anonymous linking detected, redirecting to download-app"
          );
          navigate("/download-app");
          return;
        } else {
          // Fallback: Check if user profile suggests they were previously anonymous
          try {
            const userProfileRef = doc(db, "public-profiles", user.uid);
            const userProfileSnap = await getDoc(userProfileRef);

            if (userProfileSnap.exists()) {
              const userData = userProfileSnap.data();
              // If user has quiz data but no onboarding completion, they were likely anonymous
              const hasQuizData =
                userData.firstDateQuizId ||
                userData.politicalQuizId ||
                userData.personalityQuizId ||
                userData.sexStylesQuizId;
              const needsOnboarding = !userData.onboardingCompleted;

              if (hasQuizData && needsOnboarding) {
                console.log(
                  "🔄 Fallback: User has quiz data but no onboarding, likely was anonymous, redirecting to download-app"
                );
                navigate("/download-app");
                return;
              }
            }
          } catch (error) {
            console.error("Error checking user profile for fallback:", error);
          }

          console.log("📱 Regular web user, redirecting to download");
          navigate("/download");
          return;
        }
      }

      // Native platform navigation (onboarding or home)
      console.log("📱 Native platform detected, checking user profile...");
      const userProfileRef = doc(db, "public-profiles", user.uid);
      const userProfileSnap = await getDoc(userProfileRef);

      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();

        // Check age restriction first
        if (userData.isUnder17 === true) {
          console.log("User is under 17, redirecting to age restriction");
          navigate("/age-restriction");
          return;
        }

        // Check onboarding completion
        if (userData.onboardingCompleted) {
          console.log("User has completed onboarding, navigating home.");
          navigate("/home");
        } else {
          console.log("User needs to onboard, navigating to onboarding-start.");
          navigate("/onboarding-start");
        }
      } else {
        // No profile exists yet, start onboarding
        console.log("No profile exists, navigating to onboarding-start.");
        navigate("/onboarding-start");
      }
    };

    if (currentUserState && !currentUserState.isAnonymous) {
      console.log(
        "🚀 Auth state changed to non-anonymous user, calling handleLoginSuccess"
      );
      handleLoginSuccess(currentUserState);
    }
  }, [currentUserState, navigate, location.state]);

  const sendOtp = async () => {
    // Clean and validate the phone number
    const formattedPhoneNumber = cleanAndValidatePhoneNumber(phoneNumber);
    const isNative = Capacitor.isNativePlatform();

    setIsSendingCode(true);

    try {
      if (isNative) {
        const appVerifier = window.recaptchaVerifier;
        // Use Capacitor Firebase Authentication for native iOS
        const user = auth.currentUser;

        if (isAnonymousLinking && user && user.isAnonymous) {
          await FirebaseAuthentication.linkWithPhoneNumber({
            skipNativeAuth: true,
            phoneNumber: formattedPhoneNumber,
            recaptchaVerifier: appVerifier,
          });
          console.log(
            "Phone number linking process started (native)",
            formattedPhoneNumber
          );
        } else {
          await FirebaseAuthentication.signInWithPhoneNumber({
            skipNativeAuth: true,
            phoneNumber: formattedPhoneNumber,
            recaptchaVerifier: appVerifier,
          });
          console.log(
            "Phone number sign-in process started (native)",
            formattedPhoneNumber
          );
        }
      } else {
        // Use web Firebase Authentication
        const appVerifier = window.recaptchaVerifier;
        const user = auth.currentUser;

        // If anonymous linking is required, use linkWithPhoneNumber
        if (isAnonymousLinking && user && user.isAnonymous) {
          const result = await linkWithPhoneNumber(
            user,
            formattedPhoneNumber,
            appVerifier
          );
          setConfirmationResult(result);
          setVerificationId(result.verificationId);
          setNextStep(true);
          setIsSendingCode(false);
          console.log(
            "OTP sent to phone number for anonymous linking (web)",
            formattedPhoneNumber
          );
        } else {
          // Regular phone sign-in for non-anonymous users
          const result = await signInWithPhoneNumber(
            auth,
            formattedPhoneNumber,
            appVerifier
          );
          setConfirmationResult(result);
          setVerificationId(result.verificationId);
          setNextStep(true);
          setIsSendingCode(false);
          console.log(
            "OTP sent to phone number for regular sign-in (web)",
            formattedPhoneNumber
          );
        }
      }
    } catch (error) {
      console.error("Error starting phone auth process:", error);
      setIsSendingCode(false);
    }
  };

  const verifyOtp = async () => {
    setIsVerifyingOtp(true);
    
    try {
      const isNative = Capacitor.isNativePlatform();

      console.log("🔐 Starting OTP verification:", {
        isNative,
        isAnonymousLinking,
        hasConfirmationResult: !!confirmationResult,
        verificationId,
      });

      if (isNative) {
        // First, confirm the verification code with the native plugin
        await FirebaseAuthentication.confirmVerificationCode({
          verificationId: verificationId,
          verificationCode: otp,
        });

        // Then, create a credential with the web SDK and sign in
        // This syncs the auth state to the web view, triggering onAuthStateChanged
        const credential = PhoneAuthProvider.credential(verificationId, otp);
        await signInWithCredential(auth, credential);
      } else {
        // Use web Firebase Authentication
        console.log("🌐 Web OTP verification starting...");
        const result = await confirmationResult.confirm(otp);
        console.log("✅ Web OTP verification completed:", {
          user: result.user
            ? {
                uid: result.user.uid,
                isAnonymous: result.user.isAnonymous,
                phoneNumber: result.user.phoneNumber,
              }
            : null,
        });
      }

      console.log(
        "OTP verification successful, waiting for auth state to propagate..."
      );

      // Immediate check for web platform
      if (!isNative) {
        const currentUser = auth.currentUser;
        console.log("🔍 Immediate post-OTP check:", {
          currentUser: currentUser
            ? {
                uid: currentUser.uid,
                isAnonymous: currentUser.isAnonymous,
                phoneNumber: currentUser.phoneNumber,
              }
            : null,
          isAnonymousLinking,
          platform: Capacitor.getPlatform(),
        });

        // If we have a non-anonymous user, navigate immediately
        if (currentUser && !currentUser.isAnonymous) {
          console.log("🚀 Immediate navigation - user is now non-anonymous");
          if (isAnonymousLinking) {
            console.log("➡️ Navigating to download-app for anonymous linking");
            navigate("/download-app");
            return; // Exit early to prevent the setTimeout from also firing
          } else {
            console.log("➡️ Navigating to download for regular web user");
            navigate("/download");
            return; // Exit early to prevent the setTimeout from also firing
          }
        }
      }

      // Add a manual check after a short delay to ensure navigation happens
      setTimeout(() => {
        const currentUser = auth.currentUser;
        console.log("🔄 Manual auth state check after OTP:", {
          currentUser: currentUser
            ? {
                uid: currentUser.uid,
                isAnonymous: currentUser.isAnonymous,
                phoneNumber: currentUser.phoneNumber,
              }
            : null,
          currentUserState: currentUserState
            ? {
                uid: currentUserState.uid,
                isAnonymous: currentUserState.isAnonymous,
              }
            : null,
        });

        // If we have a non-anonymous user but the useEffect hasn't triggered, manually trigger navigation
        if (
          currentUser &&
          !currentUser.isAnonymous &&
          Capacitor.getPlatform() === "web"
        ) {
          console.log(
            "🚨 Manual navigation trigger - auth state may not have propagated properly"
          );
          if (isAnonymousLinking) {
            console.log(
              "🔄 Manually navigating to download-app for anonymous linking"
            );
            navigate("/download-app");
          } else {
            console.log(
              "🔄 Manually navigating to download for regular web user"
            );
            navigate("/download");
          }
        }
      }, 2000); // Wait 2 seconds for auth state to propagate
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setIsVerifyingOtp(false);
    }
  };

  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();

    // Only set up reCAPTCHA for web platform
    if (!isNative && !window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => console.log("reCAPTCHA solved"),
        }
      );
      window.recaptchaVerifier.render();
    }
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listeners = [];

    const phoneCodeSentListener = FirebaseAuthentication.addListener(
      "phoneCodeSent",
      (event) => {
        console.log("Native: phoneCodeSent event received", event);
        setVerificationId(event.verificationId);
        setNextStep(true);
        setIsSendingCode(false);
      }
    );
    listeners.push(phoneCodeSentListener);

    const phoneVerificationFailedListener = FirebaseAuthentication.addListener(
      "phoneVerificationFailed",
      (error) => {
        console.error("Native: phoneVerificationFailed event received", error);
        setIsSendingCode(false);
      }
    );
    listeners.push(phoneVerificationFailedListener);

    // Cleanup function
    return () => {
      console.log("Cleaning up native auth listeners");
      listeners.forEach((listener) => listener.remove());
    };
  }, []);

  return (
    <LoginBody>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <LogoCircle>
                <img
                  src={logoImg}
                  alt="Vibe Check Logo"
                  style={{ height: "150px" }}
                />
              </LogoCircle>
            </TopContent>

            <BottomLogin>
              {!nextStep && (
                <div style={{ padding: "0 15px" }}>
                  <TextField
                    fullWidth
                    label="phone number"
                    variant="outlined"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    type="tel"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "17px",
                        color: OffWhite,
                        background: DarkGrey,
                        borderRadius: "15px",
                        padding: "15px 30px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "17px",
                        color: OffWhite,
                        padding: "0px 20px",
                        "&.Mui-focused": { color: OffWhite },
                        "&.MuiInputLabel-shrink": { color: OffWhite },
                      },
                      "& .Mui-focused": {
                        color: OffWhite, // focus label
                        borderRadius: "15px",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { border: "none" },
                        "&:hover fieldset": { border: "none" },
                        "&.Mui-focused fieldset": { border: "none" },
                      },
                    }}
                  />

                  <Button
                    width="100%"
                    className="mt-20"
                    $background={Black}
                    onClick={sendOtp}
                    disabled={isSendingCode}
                  >
                    {isSendingCode ? "Sending you a code..." : "Sign up or login"}
                  </Button>
                </div>
              )}

              {nextStep && (
                <div style={{ padding: "0 15px" }}>
                  <TextField
                    fullWidth
                    label="enter your code"
                    variant="outlined"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    type="tel"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 6,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "17px",
                        color: OffWhite,
                        background: DarkGrey,
                        borderRadius: "15px",
                        padding: "15px 30px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "17px",
                        color: OffWhite,
                        padding: "0px 20px",
                        "&.Mui-focused": { color: OffWhite },
                        "&.MuiInputLabel-shrink": { color: OffWhite },
                      },
                      "& .Mui-focused": {
                        color: OffWhite, // focus label
                        borderRadius: "15px",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { border: "none" },
                        "&:hover fieldset": { border: "none" },
                        "&.Mui-focused fieldset": { border: "none" },
                      },
                    }}
                  />

                  <Button
                    width="100%"
                    className="mt-20"
                    $background={Black}
                    onClick={verifyOtp}
                    disabled={isVerifyingOtp}
                  >
                    {isVerifyingOtp ? "Logging in ..." : "Login"}
                  </Button>
                </div>
              )}
            </BottomLogin>
          </Col>
        </Row>
        {!Capacitor.isNativePlatform() && <div id="recaptcha-container"></div>}
      </Container>
    </LoginBody>
  );
};

export default Login;
